'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { Heading } from '@//components/Heading';
import { LinkCloud } from '@//components/LinkCloud';
import type { MarketMetadata } from '@//market';
import styles from './SearchLinkCloud.module.scss';

interface SearchLinkCloudLocationProps {
  searchPage: string;
  marketMetadata: MarketMetadata;
  currentFilters?: {
    manufacturer?: string;
    modelSeries?: string;
    allManufacturers?: string[];
  };
}

const SearchLinkCloudLocation = ({
  searchPage,
  marketMetadata,
  currentFilters,
}: SearchLinkCloudLocationProps) => {
  const tCommon = useTranslations('other.common');
  const tSearch = useTranslations('pages.search');

  const cloudLinksLocation = useMemo(() => {
    if (!marketMetadata.locationLinkCloud) {
      return [];
    }

    // Check if we have exactly one manufacturer selected
    const manufacturerCount = currentFilters?.allManufacturers?.length ?? 0;
    const hasMultipleFilters = manufacturerCount > 1;

    return marketMetadata.locationLinkCloud.map((location) => {
      const params = new URLSearchParams();
      params.set('lat', location.lat.toString());
      params.set('lon', location.lng.toString());
      params.set('position.location', location.radius.toString());
      params.set(
        'position.name',
        `${location.name}, ${marketMetadata.region === 'sv' ? 'Sverige' : 'Norge'}`,
      );

      // Only add manufacturer and model filters if we have exactly one manufacturer
      // Don't add filters if multiple manufacturers are selected
      if (!hasMultipleFilters && currentFilters?.manufacturer) {
        params.set('manufacturer', currentFilters.manufacturer);
      }
      if (!hasMultipleFilters && currentFilters?.modelSeries) {
        params.set('modelSeries', currentFilters.modelSeries);
      }

      const href = `${searchPage}?${params.toString()}`;

      // Generate dynamic labels based on current filters
      let label: string;
      let title: string;
      let ariaLabel: string;

      // Use default labels if we have multiple manufacturers selected
      if (hasMultipleFilters) {
        // Default: multiple filters or no filters
        label = tSearch('linkCloud.vehiclesInLocation.label', {
          location: location.name,
        });
        title = tSearch('linkCloud.vehiclesInLocation.title', {
          location: location.name,
        });
        ariaLabel = title;
      } else if (currentFilters?.manufacturer && currentFilters?.modelSeries) {
        // If both manufacturer and model are filtered (single manufacturer)
        const model = `${currentFilters.manufacturer} ${currentFilters.modelSeries}`;
        label = tSearch('linkCloud.modelInLocation.label', {
          model,
          location: location.name,
        });
        title = tSearch('linkCloud.modelInLocation.title', {
          model,
          location: location.name,
        });
        ariaLabel = title;
      } else if (currentFilters?.manufacturer) {
        // If only manufacturer is filtered (single manufacturer)
        label = tSearch('linkCloud.modelInLocation.label', {
          model: currentFilters.manufacturer,
          location: location.name,
        });
        title = tSearch('linkCloud.modelInLocation.title', {
          model: currentFilters.manufacturer,
          location: location.name,
        });
        ariaLabel = title;
      } else if (currentFilters?.modelSeries) {
        // If only model is filtered (no manufacturer filter)
        label = tSearch('linkCloud.modelInLocation.label', {
          model: currentFilters.modelSeries,
          location: location.name,
        });
        title = tSearch('linkCloud.modelInLocation.title', {
          model: currentFilters.modelSeries,
          location: location.name,
        });
        ariaLabel = title;
      } else {
        // Default: no filters
        label = tSearch('linkCloud.vehiclesInLocation.label', {
          location: location.name,
        });
        title = tSearch('linkCloud.vehiclesInLocation.title', {
          location: location.name,
        });
        ariaLabel = title;
      }

      return {
        href,
        label,
        title,
        ariaLabel,
      };
    });
  }, [marketMetadata, searchPage, tSearch, currentFilters]);

  if (cloudLinksLocation.length === 0) {
    return null;
  }

  const headingId = 'search-link-cloud-heading-location';

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <Heading level={2} size="xl" id={headingId}>
        {(() => {
          const manufacturerCount = currentFilters?.allManufacturers?.length ?? 0;
          const country =
            marketMetadata.region === 'sv' ? tCommon('region.sv') : tCommon('region.nb');

          // Show manufacturer-specific heading only if exactly one manufacturer is selected
          if (manufacturerCount === 1 && currentFilters?.manufacturer) {
            return tSearch('linkCloud.findNewAndUsedManufacturerInCountry', {
              manufacturer: currentFilters.manufacturer,
              country,
            });
          }

          // Default heading for no filters or multiple manufacturers
          return tSearch('linkCloud.findNewAndUsedCarsInCountry', { country });
        })()}
      </Heading>
      <LinkCloud links={cloudLinksLocation} testId="search-link-cloud-location" />
    </section>
  );
};

export default SearchLinkCloudLocation;
